import React, { useState, useEffect } from 'react';
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
    Loader2,
    X,
    Wrench,
    Truck,
    Gauge,
    CheckCircle2,
    Search,
    ClipboardList,
    ChevronDown,
    ChevronUp,
    FileSpreadsheet,
    FileText
} from "lucide-react";
import { maintenanceService } from '../../services/maintenance/planMaestroService';
import { toast } from 'react-hot-toast';

interface RequirementItem {
    codigo_interno: string;
    nombre: string;
    cantidad: number;
    unidad_medida: string;
    precio_unitario?: number;
}

interface RoutineRequirement {
    id: number;
    nombre: string;
    tipo: string;
    frecuencia_km: number;
    km_restantes: number;
    estado: 'NORMAL' | 'ALERTA' | 'CRITICO';
    items: RequirementItem[];
    estimated_cost?: number;
}

interface VehicleRequirement {
    vehiculo: {
        id: string;
        placa: string;
        marca: string;
        modelo: string;
        plan_nombre: string;
        km_actual: number;
        total_estimated_cost?: number;
    };
    rutinas: RoutineRequirement[];
}

interface FleetRequirementsModalProps {
    open: boolean;
    onClose: () => void;
}

export const FleetRequirementsModal: React.FC<FleetRequirementsModalProps> = ({
    open,
    onClose
}) => {
    const [requirements, setRequirements] = useState<VehicleRequirement[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Estado para controlar qué vehículos tienen sus detalles expandidos
    const [expandedVehicles, setExpandedVehicles] = useState<Record<string, boolean>>({});
    const [exportingExcel, setExportingExcel] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);

    const fetchRequirements = async () => {
        try {
            setLoading(true);
            const response = await maintenanceService.getFlotaRequirements();
            setRequirements(response.data);

            // Por defecto, expandir todos los vehículos
            const initialExpanded: Record<string, boolean> = {};
            if (response.data && Array.isArray(response.data)) {
                response.data.forEach((req: VehicleRequirement) => {
                    initialExpanded[req.vehiculo.id] = true;
                });
            }
            setExpandedVehicles(initialExpanded);

        } catch (error) {
            console.error('Error fetching fleet requirements:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchRequirements();
        }
    }, [open]);

    const toggleExpand = (vehiculoId: string) => {
        setExpandedVehicles(prev => ({
            ...prev,
            [vehiculoId]: !prev[vehiculoId]
        }));
    };

    const handleExportExcel = async () => {
        try {
            setExportingExcel(true);
            const response = await maintenanceService.exportFlotaRequirementsExcel();
            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `prevision_materiales_${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("Reporte Excel descargado correctamente");
        } catch (error) {
            console.error(error);
            toast.error("Error al descargar el reporte Excel");
        } finally {
            setExportingExcel(false);
        }
    };

    const handleExportPdf = async () => {
        try {
            setExportingPdf(true);
            const response = await maintenanceService.exportFlotaRequirementsPdf();
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `prevision_materiales_${new Date().toISOString().slice(0, 10)}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("Reporte PDF descargado correctamente");
        } catch (error: any) {
            console.error(error);
            if (error.response && error.response.status === 501) {
                toast.error("La exportación PDF requiere instalar librerías adicionales en el servidor.");
            } else {
                toast.error("Error al descargar el reporte PDF");
            }
        } finally {
            setExportingPdf(false);
        }
    };

    if (!open) return null;

    const filteredRequirements = requirements.filter(req =>
        req.vehiculo.placa.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.vehiculo.modelo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.vehiculo.marca.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusColor = (estado: string) => {
        switch (estado) {
            case "CRITICO": return "text-red-700 bg-red-50 border-red-200 ring-1 ring-red-100";
            case "ALERTA": return "text-orange-700 bg-orange-50 border-orange-200 ring-1 ring-orange-100";
            default: return "text-slate-600 bg-slate-50 border-slate-200";
        }
    };

    const getStatusLabel = (rutina: RoutineRequirement) => {
        if (rutina.km_restantes < 0) return `Vencido (${Math.abs(rutina.km_restantes)} km)`;
        if (rutina.estado === 'ALERTA') return `Próximo (${rutina.km_restantes} km)`;
        return `En ${rutina.km_restantes} km`;
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(amount);
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col border border-slate-200">

                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-slate-50/50 rounded-t-xl">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-slate-800 rounded-lg shadow-md">
                            <ClipboardList className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-800">Previsión de Materiales</h2>
                            <p className="text-xs text-slate-500 font-medium">
                                Items requeridos según plan de mantenimiento
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            className="text-green-700 border-green-200 hover:bg-green-50"
                            onClick={handleExportExcel}
                            disabled={exportingExcel || loading}
                        >
                            {exportingExcel ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
                            Excel
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="text-red-700 border-red-200 hover:bg-red-50"
                            onClick={handleExportPdf}
                            disabled={exportingPdf || loading}
                        >
                            {exportingPdf ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                            PDF
                        </Button>
                        <button
                            onClick={onClose}
                            className="rounded-full p-2 hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all ml-2"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Toolbar */}
                <div className="p-4 bg-white border-b border-slate-100 flex justify-between items-center gap-4">
                    <div className="relative max-w-sm w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Filtrar por vehículo..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 h-9 text-sm border-slate-200 focus:border-blue-500 focus:ring-blue-100"
                        />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
                        <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md border border-slate-200">
                            {filteredRequirements.length}
                        </span>
                        vehículos listados
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-6 bg-slate-50/30">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <Loader2 className="h-8 w-8 animate-spin text-slate-400 mb-3" />
                            <p className="text-sm text-slate-500">Cargando datos...</p>
                        </div>
                    ) : filteredRequirements.length === 0 ? (
                        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50 mx-auto max-w-lg">
                            <CheckCircle2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                            <p className="text-base text-slate-600 font-medium">Todo en orden</p>
                            <p className="text-xs text-slate-400">No se encontraron requerimientos pendientes.</p>
                        </div>
                    ) : (
                        <div className="space-y-6 max-w-4xl mx-auto">
                            {filteredRequirements.map((req, index) => (
                                <div key={index} className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">

                                    {/* Vehicle Header */}
                                    <div
                                        className="p-4 bg-slate-50/50 border-b border-slate-100 flex justify-between items-center cursor-pointer hover:bg-slate-100/50 transition-colors"
                                        onClick={() => toggleExpand(req.vehiculo.id)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 bg-white rounded border border-slate-200 flex items-center justify-center text-slate-500">
                                                <Truck className="h-4 w-4" />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-bold text-slate-800 text-base">{req.vehiculo.placa}</h3>
                                                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 font-normal bg-slate-100 text-slate-500 border-slate-200">
                                                        {req.vehiculo.modelo}
                                                    </Badge>
                                                </div>
                                                <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                                                    <span className="flex items-center gap-1">
                                                        <Gauge className="h-3 w-3 text-slate-400" />
                                                        {req.vehiculo.km_actual?.toLocaleString()} km
                                                    </span>
                                                    <span className="text-slate-300">|</span>
                                                    <span>Plan: {req.vehiculo.plan_nombre}</span>
                                                    {req.vehiculo.total_estimated_cost !== undefined && (
                                                        <>
                                                            <span className="text-slate-300">|</span>
                                                            <span className="font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0 rounded border border-emerald-100">
                                                                Total Est.: {formatCurrency(req.vehiculo.total_estimated_cost)}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {expandedVehicles[req.vehiculo.id] ? (
                                                <ChevronUp className="h-4 w-4 text-slate-400" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4 text-slate-400" />
                                            )}
                                        </div>
                                    </div>

                                    {/* Routines List */}
                                    {expandedVehicles[req.vehiculo.id] && (
                                        <div className="divide-y divide-slate-100">
                                            {req.rutinas.map((rutina, rIdx) => (
                                                <div key={rIdx} className="p-4 hover:bg-slate-50/30 transition-colors">

                                                    {/* Routine Header */}
                                                    <div className="flex justify-between items-center mb-4">
                                                        <div className="flex items-center gap-2">
                                                            <div className="bg-blue-100 p-1.5 rounded-md">
                                                                <Wrench className="h-4 w-4 text-blue-600" />
                                                            </div>
                                                            <div>
                                                                <h4 className="font-bold text-slate-700 text-sm">{rutina.nombre}</h4>
                                                                <p className="text-[10px] text-slate-400 font-medium">
                                                                    Frecuencia: {rutina.frecuencia_km.toLocaleString()} km
                                                                </p>
                                                            </div>
                                                            <Badge variant="outline" className={`ml-2 text-[10px] h-5 font-medium ${getStatusColor(rutina.estado)}`}>
                                                                {getStatusLabel(rutina)}
                                                            </Badge>
                                                        </div>

                                                        {rutina.estimated_cost && (
                                                            <div className="text-right">
                                                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Costo Estimado</p>
                                                                <p className="text-sm font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                                                                    {formatCurrency(rutina.estimated_cost)}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Items Table */}
                                                    {rutina.items.length > 0 ? (
                                                        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden ml-9">
                                                            <table className="w-full text-xs text-left">
                                                                <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-100">
                                                                    <tr>
                                                                        <th className="px-3 py-2 w-20">CÓDIGO</th>
                                                                        <th className="px-3 py-2">DESCRIPCIÓN</th>
                                                                        <th className="px-3 py-2 text-center w-20">CANT</th>
                                                                        <th className="px-3 py-2 text-right w-24">V. UNIT</th>
                                                                        <th className="px-3 py-2 text-right w-24">SUBTOTAL</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-slate-50">
                                                                    {rutina.items.map((item, iIdx) => {
                                                                        const price = Number(item.precio_unitario) || 0;
                                                                        const subtotal = (Number(item.cantidad) || 0) * price;

                                                                        return (
                                                                            <tr key={iIdx} className="hover:bg-blue-50/20">
                                                                                <td className="px-3 py-2 text-slate-400 font-mono text-[10px]">{item.codigo_interno}</td>
                                                                                <td className="px-3 py-2 font-medium text-slate-700">{item.nombre}</td>
                                                                                <td className="px-3 py-2 text-center text-slate-600">
                                                                                    <span className="font-bold">{item.cantidad}</span> <span className="text-[10px] text-slate-400">{item.unidad_medida}</span>
                                                                                </td>
                                                                                <td className="px-3 py-2 text-right text-slate-500 font-mono">
                                                                                    {price > 0 ? formatCurrency(price) : '-'}
                                                                                </td>
                                                                                <td className="px-3 py-2 text-right font-bold text-slate-700 font-mono bg-slate-50/50">
                                                                                    {subtotal > 0 ? formatCurrency(subtotal) : '-'}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <p className="text-xs text-slate-400 italic ml-9 bg-slate-50 p-3 rounded border border-slate-100 border-dashed">
                                                            Sin repuestos configurados para esta rutina.
                                                        </p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-100 bg-slate-50/80 rounded-b-xl flex justify-end">
                    <Button onClick={onClose} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100 px-6">
                        Cerrar
                    </Button>
                </div>
            </div>
        </div>
    );
};
